import { LazyMotion, MotionConfig } from 'framer-motion'
import type { ReactNode } from 'react'

/**
 * F0-20 / PE-05 — Framer Motion entry point.
 *
 * Three constraints, the first two from §9.4:
 *   1. `LazyMotion` + the `domAnimation` feature bundle only. Importing the full
 *      `motion` component pulls the layout/drag features into the main chunk and
 *      costs roughly 3× the gzipped size against a 200KB budget.
 *   2. `reducedMotion="user"` makes `prefers-reduced-motion` global rather than
 *      per-animation, so a new component cannot forget it. The CSS side of this
 *      is in tokens.css.
 *   3. **`features` is a dynamic import, not a static one.** This is the half that
 *      makes PE-05's "feature-gated" true rather than nominal. `LazyMotion` was
 *      already in place, but `features={domAnimation}` is a static import — so the
 *      feature bundle landed in the first-paint payload anyway, ~21KB gz, on a
 *      budget that MOD-01 took to 99%. The async form defers it to a chunk that
 *      loads after paint.
 *
 * WHAT THIS COSTS: an animation that would have run in the first few hundred
 * milliseconds after mount may start a frame or two late, once the feature chunk
 * resolves. That is the right trade for a console whose animations are all
 * transitions and reveals rather than load-critical, and it is invisible to a user
 * with `prefers-reduced-motion` set.
 *
 * Consumers use the `m` namespace (`import { m } from 'framer-motion'`), which is
 * the tree-shakeable counterpart to `motion`. `strict` enforces that: it throws if
 * anyone reaches for `motion` instead, which would defeat all of the above.
 */
const loadDomAnimation = () => import('./motionFeatures').then((mod) => mod.default)

export function MotionProvider({ children }: { children: ReactNode }) {
  return (
    <LazyMotion features={loadDomAnimation} strict>
      <MotionConfig reducedMotion="user">{children}</MotionConfig>
    </LazyMotion>
  )
}

export { m, AnimatePresence, useReducedMotion } from 'framer-motion'
